import { Collapse, List, Button, Tooltip, Switch } from "antd";
import { EditOutlined, DeleteOutlined, DownOutlined, RightOutlined, CheckOutlined, CloseOutlined, EyeOutlined } from "@ant-design/icons";
import { ItemListNameWrapper, ItemNameText, ItemNameWrapper, SelectedItem, StyledBody, StyledColumn, StyledHeader, StyledIconWrapper, DragHandle } from "./styles";
import { ReactComponent as BoxIcon } from "../../assets/images/BoxIcon.svg";
import { ReactComponent as QuestionMark } from "../../assets/images/question_mark.svg";
import { ReactComponent as ConditionalQuestionIcon } from "../../assets/images/flowchart.svg";
import { StyledButton } from "../../components/styles";
import { useState } from "react";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import { EstablishmentType, ActivationStatus } from "../../types";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { updateEstablishmentType } from "../../redux/features/establishmentType/establishmentAsyncThunk";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";


export const CollapseSection = ({ itemKey, label, activeKey, children, onClick }: any) => {
    return (
        <Collapse
            size="large"
            activeKey={activeKey}
            expandIcon={({ isActive }) => (
                <div className="ant-collapse-arrow" onClick={(e) => onClick && onClick(e)}>
                    {isActive ? <DownOutlined /> : <RightOutlined />}
                </div>
            )}
            items={[
                {
                    key: itemKey,
                    label,
                    children: <StyledBody>{children}</StyledBody>,
                },
            ]}
        />
    );
};

type ItemNameProps = {
    className?: string;
    icon?: any;
    name: string;
    modalType: any;
    description?: string | null;
    showIcon?: boolean;
    showDescription?: boolean;
};

const ItemName: React.FC<ItemNameProps> = ({ className, icon, name, modalType, description, showIcon = true, showDescription = true }) => {
    return (
        <>
            <ItemNameWrapper className={className}>
                {modalType && <DragHandle>⋮⋮</DragHandle>}
                {showIcon && icon}
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <ItemNameText>{name}</ItemNameText>
                    {showDescription && description ? (
                        <span style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.2 }}>
                            {description}
                        </span>
                    ) : null}
                </div>
            </ItemNameWrapper>
        </>
    );
};

interface ItemListProps {
    dataSource: any;
    loading: boolean;
    header?: any;
    onEdit: any;
    onDelete: any;
    onItemClick?: any;
    loadMore?: any;
    isSelected?: any;
    isSelectedItemOnClick?: any;
    isStatus?: boolean;
    name?: string;
    isShowDelete?: boolean
    hasAddPermission?: boolean,
    hasEditPermission?: boolean,
    hasDeletePermission?: boolean,
    hasViewPermission?: boolean,
    handleDragEnd?: any,
    modalType?: any,
    onStatusChange?: (item: any) => Promise<void>;
    statusChecked?: (item: any) => boolean;
    statusLoading?: boolean;
}

export const ItemList: React.FC<ItemListProps> = ({ name, header, dataSource, loading, onEdit, onDelete, onItemClick, loadMore, isSelected, isSelectedItemOnClick, isStatus, isShowDelete = true, hasAddPermission, hasEditPermission, hasDeletePermission, hasViewPermission = false, handleDragEnd, modalType, onStatusChange, statusChecked, statusLoading }) => {
    const { loading: establishmentTypeLoading } = useAppSelector((state) => state.establishmentType);
    const dispatch = useAppDispatch();
    const [showDeactivationConfirm, setShowDeactivationConfirm] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<EstablishmentType | null>(null);

    const handleStatusChangeConfirm = async () => {
        try {
            if (selectedItem?.id) {
                if (onStatusChange) {
                    await onStatusChange(selectedItem);
                    setShowDeactivationConfirm(false);
                } else {
                    const formData = new FormData();
                    formData.append("name", selectedItem.name);
                    formData.append("status",
                        selectedItem.status === ActivationStatus.Active
                            ? ActivationStatus.Inactive.toString()
                            : ActivationStatus.Active.toString()
                    );

                    await dispatch(updateEstablishmentType({
                        id: selectedItem.id,
                        formData
                    })).unwrap();
                    setShowDeactivationConfirm(false);
                }
            }
        } catch (error: any) {
            toast.error(error || "Failed to change status.");
        }
    };

    return (
        <>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="droppable-list">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            <List
                                className="category-list-item"
                                loading={loading}
                                dataSource={dataSource}
                                {...(loadMore && {loadMore: loadMore})}
                                {...(header && {header: header})}
                                renderItem={(item: any, index: number) => (
                                    <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                <SelectedItem isSelected={isSelected ? item.id === isSelected : false}
                                                              onClick={() => isSelectedItemOnClick && isSelectedItemOnClick(item)}>
                                                    <List.Item
                                                        actions={[
                                                            isStatus && hasEditPermission && (
                                                                <Switch
                                                                    checkedChildren={<CheckOutlined/>}
                                                                    unCheckedChildren={<CloseOutlined/>}
                                                                    checked={statusChecked ? statusChecked(item) : item.status === 1}
                                                                    onChange={() => {
                                                                        setSelectedItem(item);
                                                                        setShowDeactivationConfirm(true);
                                                                    }}
                                                                />
                                                            ),
                                                            item?.hasSubQuestion === true ? (
                                                                <StyledIconWrapper>
                                                                    <ConditionalQuestionIcon/>
                                                                </StyledIconWrapper>
                                                            ) : null,
                                                            hasEditPermission &&
                                                            <Button size="small" icon={<EditOutlined/>}
                                                                    onClick={() => onEdit(item)}
                                                                    type="link"/>,
                                                            isShowDelete && hasDeletePermission &&
                                                            <Button size="small" icon={<DeleteOutlined/>}
                                                                    onClick={() => onDelete(item)}
                                                                    type="link" danger/>,
                                                            !hasEditPermission && !hasAddPermission && hasViewPermission &&
                                                            <Button size="small" icon={<EyeOutlined/>}
                                                                    onClick={() => onEdit(item)}
                                                                    type="link"/>,
                                                        ].filter(Boolean)}
                                                    >
                                                        <Tooltip placement="topLeft"
                                                                 title={item.name || item.fieldName}>
                                                            <ItemListNameWrapper
                                                                onClick={() => onItemClick && onItemClick()}>
                                                                <ItemName
                                                                  icon={item.title ? item?.fullUrl ? <img src={item?.fullUrl} alt={item.title} width={16} height={16}/> : <BoxIcon/> : <QuestionMark/>}
                                                                  name={item.title || item.fieldName || item.name}
                                                                  description={item.description}
                                                                  showIcon={false}
                                                                  showDescription={true}
                                                                  modalType={modalType}
                                                                />
                                                            </ItemListNameWrapper>
                                                        </Tooltip>
                                                    </List.Item>
                                                </SelectedItem>
                                            </div>
                                        )}
                                    </Draggable>
                                )}
                            />
                            {showDeactivationConfirm && (
                                <ConfirmationModal
                                    handleSave={handleStatusChangeConfirm}
                                    isModalOpen={showDeactivationConfirm}
                                    onCancel={() => setShowDeactivationConfirm(false)}
                                    title={`Confirm ${(statusChecked ? statusChecked(selectedItem) : selectedItem?.status === ActivationStatus.Active) ? "Deactivation" : "Activation"} ${name}`}
                                    message={`Are you sure you want to ${(statusChecked ? statusChecked(selectedItem) : selectedItem?.status === ActivationStatus.Active) ? "deactivate" : "activate"} ${name}?`}
                                    confirmButtonText={(statusChecked ? statusChecked(selectedItem) : selectedItem?.status === ActivationStatus.Active) ? "Deactivate" : "Activate"}
                                    loading={statusLoading !== undefined ? statusLoading : establishmentTypeLoading}
                                />
                            )}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </>
    );
};

interface ItemLabelProps {
    title: string;
    buttonLabel: string;
    onButtonClick?: any;
    onTitleClick?: any;
    hasAddPermission: boolean,
}

export const ItemLabel: React.FC<ItemLabelProps> = ({ title, buttonLabel, onButtonClick, onTitleClick, hasAddPermission }) => {
    return (
        <StyledHeader onClick={() => onTitleClick && onTitleClick()}>
            <span>{title}</span>
            {(hasAddPermission) && <StyledButton
                type="primary"
                onClick={(e) => {
                    onButtonClick && onButtonClick(e);
                }}
            >
                {buttonLabel}
            </StyledButton>}
        </StyledHeader>
    );
}

interface ItemColumnProps {
    title: string;
    buttonLabel: string;
    onAddClick: () => void;
    dataSource: any;
    loading: boolean;
    header?: any;
    onEdit: any;
    onDelete: any;
    onItemClick?: any;
    loadMore?: any;
    isSelected?: any;
    isSelectedItemOnClick?: any;
    isStatus?: boolean;
    handleDragEnd?: any;
    modalType?: any;
    isShowDelete?: boolean,
    hasAddPermission: boolean,
    hasEditPermission: boolean,
    hasDeletePermission: boolean
    hasViewPermission: boolean
    onStatusChange?: (item: any) => Promise<void>;
    statusChecked?: (item: any) => boolean;
    statusLoading?: boolean;
}

export const ItemColumn: React.FC<ItemColumnProps> = ({ title, buttonLabel, onAddClick, dataSource, loading, isSelected, isSelectedItemOnClick, onEdit, onDelete, header, loadMore, onItemClick, isStatus, isShowDelete = true, hasAddPermission, hasEditPermission, hasDeletePermission, hasViewPermission = false, handleDragEnd, modalType, onStatusChange, statusChecked, statusLoading }) => (
    <StyledColumn>
        <ItemLabel title={title} buttonLabel={buttonLabel} onButtonClick={onAddClick} hasAddPermission={hasAddPermission} />
        <StyledBody>
            <ItemList
                name={title}
                isStatus={isStatus}
                header={header}
                loading={loading}
                dataSource={dataSource}
                isSelected={isSelected}
                isSelectedItemOnClick={isSelectedItemOnClick}
                onEdit={onEdit}
                hasEditPermission={hasEditPermission}
                hasDeletePermission={hasDeletePermission}
                hasViewPermission={hasViewPermission}
                onDelete={onDelete}
                loadMore={loadMore}
                onItemClick={onItemClick}
                isShowDelete={isShowDelete}
                handleDragEnd={handleDragEnd}
                modalType={modalType}
                onStatusChange={onStatusChange}
                statusChecked={statusChecked}
                statusLoading={statusLoading}
            />
        </StyledBody>
    </StyledColumn>
);
